/**
 * Pure helpers for the existing-resource pickers on the New Deployment wizard. Everything
 * follows the hierarchy environment -> product -> location -> resource group (-> VNet ->
 * subnet): `getProductResources` resolves the first two levels and returns undefined when
 * no data has been supplied for that environment/product, and every other helper narrows
 * from there. Also holds the Customer ID and SQL instance rules.
 */
import {
  RESOURCE_CATALOG,
  TRAVERSE_EXISTING_CUSTOMER_IDS,
  TRAVERSE_REGION_LABELS,
  type ProductResources,
  type TraverseSqlServer,
  type TraverseSubnet,
  type TraverseVnet,
} from '@/data/traverseProduction'

export type TraverseDatabaseMode = 'existing' | 'new'

/** Database choice on the Infrastructure step. */
export interface TraverseDatabase {
  mode: TraverseDatabaseMode
  /** Existing mode: the database server and its resource group. */
  server_name: string
  server_resource_group: string
  /** Existing mode: the new SQL instance created inside that server. */
  instance_name: string
  // New mode: fields TBD, intentionally none yet.
}

export const DEFAULT_TRAVERSE_DATABASE: TraverseDatabase = {
  mode: 'existing',
  server_name: '',
  server_resource_group: '',
  instance_name: '',
}

/** The location -> resource group -> VNet -> subnet picks, as the form holds them. */
export interface NetworkSelection {
  region: string
  resourceGroup: string
  vnet: string
  subnet: string
}

export interface NetworkProblem {
  /** 'incomplete' = a level is still to be picked; 'unavailable' = nothing usable to pick. */
  kind: 'incomplete' | 'unavailable'
  message: string
}

/** A customer ID is 3 characters: letters or digits. Stored uppercase. */
const CUSTOMER_ID_RE = /^[A-Z0-9]{3}$/

/** Azure reserves the first four and the last address of every subnet. */
const AZURE_RESERVED_IPS = 5

// ─── Hierarchy: environment -> product ────────────────────────────────────────

/** Resources for one product in one environment, or undefined when none were supplied. */
export function getProductResources(environment: string, productSlug: string): ProductResources | undefined {
  return RESOURCE_CATALOG[environment]?.[productSlug]
}

// ─── Location ─────────────────────────────────────────────────────────────────

export function regionLabelFromCode(code: string): string {
  return TRAVERSE_REGION_LABELS[code] ?? code
}

/** Display name (as used by azure_region) -> location code. Unknown names yield ''. */
export function regionCodeFromLabel(label: string): string {
  const entry = Object.entries(TRAVERSE_REGION_LABELS).find(([, name]) => name === label)
  return entry ? entry[0] : ''
}

const unique = <T>(values: T[]): T[] => [...new Set(values)]

/** Locations that have at least one existing VNet, in catalog order. */
export function getLocations(res: ProductResources | undefined): Array<{ code: string; label: string }> {
  return unique((res?.networks ?? []).map((v) => v.region)).map((code) => ({ code, label: regionLabelFromCode(code) }))
}

// ─── Resource group -> VNet -> subnet ─────────────────────────────────────────

export function getResourceGroups(res: ProductResources | undefined, region: string): string[] {
  return unique((res?.networks ?? []).filter((v) => v.region === region).map((v) => v.resourceGroup))
}

/**
 * The Azure portal's auto-created default VNet carries a subnet literally named "default".
 * It is a placeholder, not an existing VNet to deploy into, so it is never offered.
 */
export function isDefaultVnet(vnet: TraverseVnet): boolean {
  return vnet.subnets.some((s) => s.name.toLowerCase() === 'default')
}

/** Existing VNets of a resource group in a location. Default VNets are excluded. */
export function getVnets(res: ProductResources | undefined, region: string, resourceGroup: string): TraverseVnet[] {
  return (res?.networks ?? []).filter((v) => v.region === region && v.resourceGroup === resourceGroup && !isDefaultVnet(v))
}

/** Selectable subnets of a VNet. Gateway subnets are never offered. */
export function getSubnets(
  res: ProductResources | undefined, region: string, resourceGroup: string, vnetName: string,
): TraverseSubnet[] {
  const vnet = getVnets(res, region, resourceGroup).find((v) => v.name === vnetName)
  return vnet ? vnet.subnets.filter((s) => !s.isGateway) : []
}

/** Addresses a subnet can still hand out: its size less Azure's reserved five, less what is in use. */
export function subnetFreeIps(subnet: TraverseSubnet): number {
  const prefixLength = Number(subnet.prefix.split('/')[1])
  if (!Number.isInteger(prefixLength) || prefixLength < 0 || prefixLength > 32) return 0
  return Math.max(0, 2 ** (32 - prefixLength) - AZURE_RESERVED_IPS - subnet.usedIps)
}

/**
 * First reason the picked network cannot be used for `hostCount` session hosts, or null
 * when it is ready: a level not picked yet, no existing VNet to pick, or no subnet with
 * enough free IPs.
 */
export function getNetworkProblem(
  res: ProductResources | undefined, selection: NetworkSelection, hostCount: number,
): NetworkProblem | null {
  const { region, resourceGroup, vnet, subnet } = selection
  const hosts = `${hostCount} session host${hostCount === 1 ? '' : 's'}`
  if (!getLocations(res).some((l) => l.code === region)) return { kind: 'incomplete', message: 'Select a location.' }
  if (!getResourceGroups(res, region).includes(resourceGroup)) return { kind: 'incomplete', message: 'Select a resource group.' }
  const vnets = getVnets(res, region, resourceGroup)
  if (vnets.length === 0) {
    return {
      kind: 'unavailable',
      message: `No existing VNet is available in ${resourceGroup} (${regionLabelFromCode(region)}). `
        + 'Choose another resource group or location, or ask the network team to create one.',
    }
  }
  if (!vnets.some((v) => v.name === vnet)) return { kind: 'incomplete', message: 'Select a VNet.' }
  const subnets = getSubnets(res, region, resourceGroup, vnet)
  if (!subnets.some((s) => subnetFreeIps(s) >= hostCount)) {
    return {
      kind: 'unavailable',
      message: subnets.length === 0
        ? `${vnet} has no subnet to deploy into.`
        : `No subnet in ${vnet} has space for ${hosts}. Choose another VNet or reduce the session host count.`,
    }
  }
  const picked = subnets.find((s) => s.name === subnet)
  if (!picked) return { kind: 'incomplete', message: 'Select a subnet.' }
  const free = subnetFreeIps(picked)
  if (free < hostCount) {
    return {
      kind: 'unavailable',
      message: `${picked.name} has ${free === 0 ? 'no free IPs' : `only ${free} free IP${free === 1 ? '' : 's'}`}, `
        + `not enough for ${hosts}. Choose another subnet or reduce the session host count.`,
    }
  }
  return null
}

/**
 * Default NSG for a subnet: the product's `subnetDefaultNsg` lookup first, then the subnet's
 * own `defaultNsg`. '' while neither has been supplied.
 */
export function getDefaultNsg(res: ProductResources | undefined, vnetName: string, subnetName: string): string {
  const fromLookup = res?.subnetDefaultNsg[`${vnetName}/${subnetName}`]
  if (fromLookup) return fromLookup
  const subnet = (res?.networks ?? []).flatMap((v) => (v.name === vnetName ? v.subnets : []))
    .find((s) => s.name === subnetName)
  return subnet?.defaultNsg ?? ''
}

// ─── Database server ──────────────────────────────────────────────────────────

/** Existing database servers in a location, for the selected environment and product. */
export function getSqlServers(res: ProductResources | undefined, region: string): TraverseSqlServer[] {
  return (res?.sqlServers ?? []).filter((s) => s.region === region)
}

/** Error text for a new SQL instance name on `server`, or null when it is acceptable. */
export function validateInstanceName(server: TraverseSqlServer | undefined, rawName: string): string | null {
  const name = rawName.trim()
  if (!name) return 'SQL instance name is required.'
  if (server && server.existingInstances.some((existing) => existing.toLowerCase() === name.toLowerCase())) {
    return `An instance named “${name}” already exists on ${server.name}.`
  }
  return null
}

/**
 * First problem with the database choice, or null when it is complete. New-server mode
 * has no fields yet (TODO), so it never blocks.
 */
export function validateTraverseDatabase(
  db: TraverseDatabase, res: ProductResources | undefined, region: string,
): string | null {
  if (db.mode === 'new') return null
  const server = getSqlServers(res, region).find((s) => s.name === db.server_name)
  if (!server) return 'Select an existing database server.'
  return validateInstanceName(server, db.instance_name)
}

// ─── Customer ID ──────────────────────────────────────────────────────────────

/** Customer IDs are stored uppercase. */
export function normalizeCustomerId(raw: string): string {
  return raw.trim().toUpperCase()
}

/** Error text for a Customer ID, or null when it is acceptable. */
export function validateCustomerId(rawId: string): string | null {
  const id = normalizeCustomerId(rawId)
  if (!id) return 'Customer ID is required.'
  if (!CUSTOMER_ID_RE.test(id)) return 'Customer ID must be exactly 3 letters or digits.'
  if (TRAVERSE_EXISTING_CUSTOMER_IDS.includes(id)) return `Customer ID ${id} is already in use.`
  return null
}

/** SYS DB = SYS + ID, Main DB = ID. Empty strings until the ID is valid. */
export function deriveCustomerDbNames(rawId: string): { sysDb: string; mainDb: string } {
  if (validateCustomerId(rawId) !== null) return { sysDb: '', mainDb: '' }
  const id = normalizeCustomerId(rawId)
  return { sysDb: `SYS${id}`, mainDb: id }
}
